import { DecorationSet } from 'prosemirror-view';
import type { EditorView } from 'prosemirror-view';

import { buildGrammarDecorations, grammarPluginKey } from './grammarPlugin';
import type { BlockLintFn, GrammarIssue, PlainLint } from './types';

const LINT_DEBOUNCE_MS = 800;
const CACHE_MAX_BLOCKS = 2000;

type CheckerOptions = {
  view: EditorView;
  lintFn: BlockLintFn;
  /** Linting is skipped (and decorations cleared) while this returns true. */
  isPaused?: () => boolean;
  onIssues?: (issues: GrammarIssue[]) => void;
};

/**
 * Debounced lint loop: walks the doc's textblocks, lints each through
 * `lintFn` (cached by block text, so untouched paragraphs are free) and
 * publishes inline decorations through the grammar plugin.
 */
export class GrammarChecker {
  private readonly view: EditorView;
  private readonly lintFn: BlockLintFn;
  private readonly isPaused: () => boolean;
  private readonly onIssues?: (issues: GrammarIssue[]) => void;

  private cache = new Map<string, PlainLint[]>();
  private ignored = new Set<string>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private rerunRequested = false;
  private destroyed = false;

  // Overridable in tests so the debounce does not use a real timer.
  debounceMs = LINT_DEBOUNCE_MS;

  constructor({ view, lintFn, isPaused, onIssues }: CheckerOptions) {
    this.view = view;
    this.lintFn = lintFn;
    this.isPaused = isPaused ?? (() => false);
    this.onIssues = onIssues;
  }

  schedule() {
    if (this.destroyed) return;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.run();
    }, this.debounceMs);
  }

  async run(): Promise<void> {
    if (this.destroyed) return;
    if (this.running) {
      this.rerunRequested = true;
      return;
    }
    if (this.isPaused()) {
      this.publish([]);
      return;
    }

    this.running = true;
    try {
      const doc = this.view.state.doc;
      const blocks: Array<{ pos: number; text: string; nodeType: string; inList: boolean }> = [];
      doc.descendants((node, pos, parent) => {
        if (node.isTextblock) {
          blocks.push({
            pos,
            text: node.textBetween(0, node.content.size, undefined, '\n'),
            nodeType: node.type.name,
            inList: parent?.type.name === 'list_item',
          });
          return false;
        }
        return true;
      });

      const issues: GrammarIssue[] = [];
      for (const block of blocks) {
        if (!block.text.trim()) continue;
        const cacheKey = `${block.nodeType} ${block.text}`;
        let lints = this.cache.get(cacheKey);
        if (!lints) {
          lints = await this.lintFn(block.text, { nodeType: block.nodeType, inList: block.inList });
          if (this.destroyed) return;
          if (this.cache.size >= CACHE_MAX_BLOCKS) this.cache.clear();
          this.cache.set(cacheKey, lints);
        }
        for (const lint of lints) {
          if (this.ignored.has(ignoreKey(lint))) continue;
          issues.push({
            ...lint,
            from: block.pos + 1 + lint.start,
            to: block.pos + 1 + lint.end,
          });
        }
      }

      // The doc changed while linting was awaited — these positions are
      // stale, throw them away and lint the current doc instead.
      if (this.view.state.doc !== doc) {
        this.rerunRequested = true;
        return;
      }

      this.publish(issues);
    } finally {
      this.running = false;
      if (this.rerunRequested && !this.destroyed) {
        this.rerunRequested = false;
        this.schedule();
      }
    }
  }

  /** Suppress this lint (matched by kind + flagged text) for the session. */
  ignore(issue: GrammarIssue) {
    this.ignored.add(ignoreKey(issue));
    void this.run();
  }

  clear() {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.publish([]);
  }

  destroy() {
    this.destroyed = true;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private publish(issues: GrammarIssue[]) {
    if (this.destroyed) return;
    const decorations = DecorationSet.create(
      this.view.state.doc,
      buildGrammarDecorations(issues),
    );
    this.view.dispatch(this.view.state.tr.setMeta(grammarPluginKey, { decorations }));
    this.onIssues?.(issues);
  }
}

function ignoreKey(lint: PlainLint): string {
  return `${lint.kind}|${lint.problemText}`;
}
